/**
 * Public Trust & Data Safety page. Renders the currently published
 * version of the Trust document plus a downloadable PDF brief.
 *
 * Analytics are privacy-respecting: a single anonymous visit ping on
 * mount, a single anonymous download ping on PDF click. No IP, no
 * cookies, no user identifier stored.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Shield, FileText, AlertTriangle } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import api from "@/lib/api";

export default function Trust() {
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    api.get("/trust/document").then((r) => setDoc(r.data)).catch(() => setDoc(null));
    api.post("/trust/analytics/visit").catch(() => {});
  }, []);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const onDownload = () => {
    api.post("/trust/analytics/download").catch(() => {});
    window.open(`${backend}/api/trust/pdf`, "_blank");
  };

  if (!doc) {
    return (
      <div className="fl-page min-h-screen">
        <PublicHeader />
        <div className="max-w-4xl mx-auto px-6 pt-32 pb-24 text-zinc-400" data-testid="trust-loading">
          Loading Trust & Data Safety information…
        </div>
        <PublicFooter />
      </div>
    );
  }

  const c = doc.content || {};
  const sections = c.sections || [];

  return (
    <div className="fl-page min-h-screen">
      <PublicHeader />

      <section className="pt-24 pb-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="fl-label text-[#f47a20] flex items-center gap-2"><Shield size={12} /> Trust & Data Safety</div>
          <h1 className="fl-heading text-5xl md:text-6xl mt-3" data-testid="trust-heading">
            Your data. Your control.
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl">
            How Functional LAB protects clinical information, how users retain control
            over their data, and how the platform is designed to remain portable if
            hosting providers change.
          </p>

          <div className="mt-6 fl-card p-4 border-l-4" style={{ borderLeftColor: "#F59E0B", background: "color-mix(in oklab, #F59E0B 4%, transparent)" }} data-testid="trust-preclinical-warning">
            <div className="flex items-start gap-2 text-sm text-amber-200">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-400" />
              <span>{c.warnings?.pre_clinical}</span>
            </div>
          </div>

          <div className="mt-4 fl-card p-4 text-sm text-zinc-300" data-testid="trust-positioning">
            {c.positioning_statement}
          </div>

          <div className="mt-4 fl-card p-4 text-sm text-zinc-300" data-testid="trust-portability-wording">
            {c.portability_wording}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={onDownload}
              className="fl-btn-anim fl-btn justify-center text-sm"
              data-testid="trust-download-pdf"
            >
              <Download size={14} /> Download Data Safety Brief (PDF)
            </button>
            <Link to="/contact" className="fl-btn-anim fl-btn-ghost justify-center text-sm" data-testid="trust-contact">
              <FileText size={14} /> Request more information
            </Link>
          </div>

          <div className="mt-3 text-xs text-zinc-500" data-testid="trust-metadata">
            Version {doc.version} · Last reviewed {(doc.last_review_date || "").slice(0, 10) || "—"} ·
            {doc.owner ? ` Owner: ${doc.owner}` : ""}
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="fl-label text-[#f47a20] mb-4">Contents</div>
          <div className="grid md:grid-cols-2 gap-2 text-sm">
            {sections.map((s, i) => (
              <a key={s.key} href={`#sec-${s.key}`} className="text-zinc-300 hover:text-[#f47a20]" data-testid={`trust-toc-${s.key}`}>
                {i + 1}. {s.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6 space-y-6">
          {sections.map((s, i) => (
            <div key={s.key} id={`sec-${s.key}`} className="fl-card p-6" data-testid={`trust-sec-${s.key}`}>
              <div className="fl-label text-[#f47a20]">Section {i + 1}</div>
              <h2 className="fl-heading text-2xl mt-1">{s.title}</h2>
              <p className="text-sm text-zinc-300 leading-relaxed mt-3 whitespace-pre-line">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6 fl-card p-6 text-center">
          <div className="fl-label text-[#f47a20]">Security incident?</div>
          <div className="fl-heading text-2xl md:text-3xl mt-2">Report it responsibly.</div>
          <p className="text-sm text-zinc-400 mt-2">
            Email <a className="text-[#f47a20] hover:underline" href="mailto:functional.lab25@gmail.com">functional.lab25@gmail.com</a>.
            Data-rights requests: <a className="text-[#f47a20] hover:underline" href="mailto:functional.lab25@gmail.com">functional.lab25@gmail.com</a>.
          </p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
